import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import type React from 'react';
import * as Icon from 'react-bootstrap-icons';

import { getCaptchaRequestUrl, mapCaptchaApiResponse } from './helpers';
import {
  type DNTCaptchaApiResponse,
  type DNTCaptchaParams,
  type DNTCaptchaProps,
  type DNTCaptchaRef,
  type DNTCaptchaState
} from './types';

const DNTCaptcha = (props: DNTCaptchaProps): React.ReactElement => {
  const [state, setState] = useState<DNTCaptchaState>({
    apiResponse: undefined,
    DNTCaptchaInputText: '',
    isLoading: true
  });

  const requestUrl = useMemo(() => getCaptchaRequestUrl(props.realm), [props.realm]);

  const loadCaptcha = useCallback(async () => {
    setState((currentState) => ({ ...currentState, isLoading: true }));
    props.onLoadStateChange?.(false);

    try {
      const response = await fetch(requestUrl, {
        credentials: 'include',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Unable to load captcha: ${response.status}`);
      }

      const data = (await response.json()) as DNTCaptchaApiResponse;

      setState((currentState) => ({
        ...currentState,
        apiResponse: mapCaptchaApiResponse(data, props.realm),
        DNTCaptchaInputText: '',
        isLoading: false
      }));
      props.onLoadStateChange?.(true);
    } catch (error) {
      console.error('Failed to load DNTCaptcha', error);
      setState((currentState) => ({
        ...currentState,
        apiResponse: undefined,
        DNTCaptchaInputText: '',
        isLoading: false
      }));
      props.onLoadStateChange?.(false);
    }
  }, [props.onLoadStateChange, props.realm, requestUrl]);

  useEffect(() => {
    void loadCaptcha();
  }, [loadCaptcha]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setState((currentState) => ({
      ...currentState,
      [event.target.id]: event.target.value
    }));
  };

  const { apiResponse } = state;

  useEffect(() => {
    const dntCaptchaRef: DNTCaptchaRef = {
      getResponse: () => {
        return {
          DNTCaptchaInputText: state.DNTCaptchaInputText,
          DNTCaptchaText: apiResponse?.dntCaptchaTextValue,
          DNTCaptchaToken: apiResponse?.dntCaptchaTokenValue
        } as DNTCaptchaParams;
      },
      refresh: loadCaptcha
    };

    props.dntCaptchaRef(dntCaptchaRef);
  }, [
    apiResponse?.dntCaptchaTextValue,
    apiResponse?.dntCaptchaTokenValue,
    loadCaptcha,
    props,
    state.DNTCaptchaInputText
  ]);

  if (!apiResponse) {
    if (state.isLoading) {
      return <div aria-live="polite">Loading captcha...</div>;
    }

    return (
      <div className="invalid-feedback d-block" role="alert">
        Unable to load captcha. Refresh the page and try again.
        <button className="btn btn-link p-0 ms-1" type="button" onClick={() => void loadCaptcha()}>
          Retry captcha
        </button>
      </div>
    );
  }

  return (
    <div className="dntCaptcha my-2" id={apiResponse.dntCaptchaId}>
      <img
        alt="captcha"
        id="dntCaptchaImg"
        src={apiResponse.dntCaptchaImgUrl}
        style={{ marginBottom: '4px' }}
      />
      <Icon.ArrowCounterclockwise
        className="ms-2"
        style={{ cursor: 'pointer' }}
        id="dntCaptchaRefreshButton"
        onClick={() => void loadCaptcha()}
      />
      <input
        id="DNTCaptchaText"
        name="DNTCaptchaText"
        type="hidden"
        value={apiResponse.dntCaptchaTextValue}
      />
      <div className="input-group row">
        <input
          autoComplete="off"
          className="text-box single-line form-control"
          dir="ltr"
          id="DNTCaptchaInputText"
          name="DNTCaptchaInputText"
          placeholder={props.placeholder}
          type="text"
          value={state.DNTCaptchaInputText}
          onChange={handleChange}
        />
      </div>
      <input
        id="DNTCaptchaToken"
        name="DNTCaptchaToken"
        type="hidden"
        value={apiResponse.dntCaptchaTokenValue}
      />
    </div>
  );
};

export default DNTCaptcha;
